import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Icon, type IconName } from '../assets/icons';
import { cn } from '../lib/cn';
import { Loader } from './loader';
import { Typography, type TypographyVariant } from './typography';

const shapeClasses: Record<ButtonShape, string> = {
  standart: 'rounded-xs',
};
const sizeClasses: Record<ButtonSize, string> = {
  small: 'px-2',
  medium: 'px-4 py-1.5',
  large: 'px-4 py-2',
  xl: '',
};

const iconedSizeClasses: Record<ButtonSize, string> = {
  small: 'p-0.5',
  medium: 'p-2',
  large: '',
  xl: '',
};

const iconSize: Record<ButtonSize, number> = {
  small: 14,
  medium: 16,
  large: 18,
  xl: 24,
};
const variantClasses: Record<ButtonVariant, string> = {
  primary: `
  bg-primary-6 text-primary-inverse shadow-[0_2px_0_0_rgba(0,0,0,0.04)]
  hover:bg-primary-8 
  focus-visible:none
  disabled:bg-neutral-3 disabled:text-gray-400 disabled:border disabled:border-neutral-5`,
  secondary: `
  bg-neutral-1 border border-neutral-5
  hover:text-primary-5 hover:border-primary-5
  disabled:bg-neutral-3 disabled:text-gray-400 disabled:border disabled:border-neutral-5
  `,
  link: `text-primary-text 
  hover:text-primary-8 dark:hover:text-primary-inverse
  focus-visible:none
  disabled:text-neutral-3 disabled:text-gray-400`,
  text: `
  hover:bg-neutral-2
  disabled:text-gray-400
  `,
};
const dangerousClasses: Record<ButtonVariant, string> = {
  primary: `
  bg-dust-red-6 text-primary-inverse
  hover:bg-dust-red-4
  `,
  secondary: `
  border border-character-danger text-character-danger
  hover:text-dust-red-4 hover:border-dust-red-4
  `,
  link: `text-character-danger
  hover:text-character-danger`,
  text: `
  text-character-danger
  hover:bg-character-danger
  `,
};

const activeClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary-7',
  secondary: 'text-primary-7 border-primary-7',
  link: 'text-primary-7 dark:text-primary-text',
  text: 'bg-neutral-3',
};

const typographyVariantBySize: Record<ButtonSize, TypographyVariant> = {
  small: 'body',
  medium: 'body',
  large: 'h5',
  xl: 'body',
};

export type ButtonShape = 'standart';
export type ButtonVariant = 'primary' | 'secondary' | 'link' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large' | 'xl';

type ButtonProps = {
  shape?: ButtonShape;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  fullWidth?: boolean;
  dangerous?: boolean;
  leftIconName?: IconName;
  rightIconName?: IconName;
  loaderPlacement?: 'left' | 'right';
  iconed?: boolean;
} & ComponentPropsWithoutRef<'button'>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      size = 'medium',
      shape = 'standart',
      variant = 'primary',
      loaderPlacement = 'right',
      loading = false,
      disabled = false,
      active = false,
      fullWidth = false,
      iconed = false,
      dangerous = false,
      children,
      className,
      leftIconName,
      rightIconName,
      ...props
    },
    ref
  ) => {
    return (
      <button
        {...props}
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(
          'flex w-fit items-center justify-center',
          fullWidth && 'w-full',
          shapeClasses[shape],
          variantClasses[variant],
          !iconed && 'gap-2',
          !iconed ? sizeClasses[size] : iconedSizeClasses[size],
          dangerous && dangerousClasses[variant],
          active && activeClasses[variant],
          className
        )}
      >
        {!loading && leftIconName && (
          <Icon name={leftIconName} size={iconSize[size]} />
        )}
        {loaderPlacement === 'left' && loading && (
          <Loader currentColor size={iconSize[size]} />
        )}

        {!iconed && (
          <Typography weight="regular" variant={typographyVariantBySize[size]}>
            {children}
          </Typography>
        )}

        {loaderPlacement === 'right' && loading && (
          <Loader currentColor size={iconSize[size]} />
        )}
        {!loading && rightIconName && (
          <Icon name={rightIconName} size={iconSize[size]} />
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
